import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useLegalAuth as useAuth } from '../hooks/useLegalAuth';
import { getLegalSettings, recordConsent, getClientLocation } from '../components/services/consentService';

const TncReview = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [error, setError] = useState('');
  const [locationNotice, setLocationNotice] = useState('');
  const [cityOverride, setCityOverride] = useState('');
  const [stateOverride, setStateOverride] = useState('');
  const { profileDocId } = useAuth();

  const populateDetectedLocation = async () => {
    const detected = await getClientLocation();
    if (!cityOverride.trim() && detected.city && detected.city !== 'unknown') {
      setCityOverride(detected.city);
    }
    if (!stateOverride.trim() && detected.state && detected.state !== 'unknown') {
      setStateOverride(detected.state);
    }
    return detected;
  };

  const fetchSettings = async () => {
    setLoadingSettings(true);
    setError('');
    try {
      const legal = await getLegalSettings();
      if (!legal || !legal.tncContent) {
        setSettings(null);
        setError('The Terms & Conditions could not be loaded right now. Please try again in a moment.');
      } else {
        setSettings(legal);
      }
    } catch (err) {
      console.error('Legal settings load failed:', err);
      setSettings(null);
      setError('The Terms & Conditions could not be loaded. Please check your connection and try again.');
    } finally {
      setLoadingSettings(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  useEffect(() => {
    if (!settings || loadingSettings) return;
    (async () => {
      try {
        const geoPermission = navigator.permissions
          ? await navigator.permissions.query({ name: 'geolocation' }).catch(() => null)
          : null;

        if (geoPermission?.state === 'denied') {
          setLocationNotice('Location access is blocked in this browser. Please enter your city and state manually below.');
          return;
        }

        setLocationNotice('Detecting your city and state automatically. You can correct it below before accepting.');
        await populateDetectedLocation();
      } catch (err) {
        console.warn('Location permission lookup failed:', err);
        setLocationNotice('We could not detect your location automatically. Please enter it manually below.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, loadingSettings]);

  const handleAccept = async () => {
    if (!profileDocId) {
      alert('Your profile is still loading. Please wait a moment and try again.');
      return;
    }
    if (!settings?.tncContent) {
      alert('No Terms & Conditions content is available right now. Please refresh and try again.');
      return;
    }

    setAccepting(true);
    try {
      let city = cityOverride.trim();
      let state = stateOverride.trim();
      let ipAddress = 'manual-entry';

      if (!city && !state) {
        const fallback = await getClientLocation();
        city = fallback.city !== 'unknown' ? fallback.city : '';
        state = fallback.state !== 'unknown' ? fallback.state : '';
        ipAddress = fallback.ipAddress;
      }

      const manualLocation = { city: city || 'unknown', state: state || 'unknown', ipAddress };

      await recordConsent(profileDocId, manualLocation);
      window.location.href = '/';
    } catch (err) {
      console.error('T&C acceptance failed:', err);
      alert(err.message || 'Unable to record your T&C acceptance. Please try again.');
      setAccepting(false);
    }
  };

  const backButton = (
    <Button
      variant="text"
      onClick={() => navigate('/profile-legal')}
      sx={{
        minWidth: 0, width: 32, height: 32, borderRadius: '50%', color: '#4F46E5',
        background: 'transparent', p: 0, ml: -1.5,
        '&:hover': { background: '#F4F1FF', color: '#382EC7' },
      }}
      aria-label="Back to legal page"
    >
      <ArrowBackIcon sx={{ fontSize: 20 }} />
    </Button>
  );

  if (loadingSettings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress sx={{ color: '#4F46E5' }} />
      </Box>
    );
  }

  if (error || !settings) {
    return (
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, borderBottom: '1px solid #E9E7F2', minHeight: 86 }}>
          {backButton}
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1A0F2E', letterSpacing: '-0.04em', whiteSpace: 'nowrap', fontSize: { xs: '1.8rem', sm: '2.1rem' } }}>
            Terms & Conditions
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 720, mx: 'auto', p: 4, borderRadius: 3, border: '1px solid #E4E0F9', background: 'linear-gradient(180deg, #F9F8FF 0%, #F4F7FF 100%)' }}>
          <Typography sx={{ mb: 3, color: '#6B6480' }}>{error || 'Not available right now.'}</Typography>
          <Button variant="contained" onClick={fetchSettings} sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, borderBottom: '1px solid #E9E7F2', minHeight: 86 }}>
        {backButton}
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1A0F2E', letterSpacing: '-0.04em', whiteSpace: 'nowrap', fontSize: { xs: '1.8rem', sm: '2.1rem' } }}>
          Terms & Conditions
        </Typography>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid #E4E0F9', background: 'linear-gradient(180deg, #F9F8FF 0%, #F4F7FF 100%)', boxShadow: '0 12px 30px rgba(79, 70, 229, 0.06)' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#1A0F2E' }}>
          Please review and accept our Terms & Conditions
        </Typography>
        <Typography sx={{ mb: 2, color: '#6B6480', lineHeight: 1.7 }}>
          Version {settings.tncVersion}
          {settings.tncUpdatedAt?.toDate ? ` — last updated ${settings.tncUpdatedAt.toDate().toLocaleDateString()}` : ''}.
          By accepting, you also acknowledge our{' '}
          <a href="/tnc" target="_blank" rel="noopener noreferrer" style={{ color: '#4F46E5', fontWeight: 600 }}>Privacy Policy</a>.
        </Typography>
        <Typography sx={{ mb: 2, color: '#5C5670', fontSize: 13, lineHeight: 1.7, background: '#F3F0FF', border: '1px solid #E4E0F9', borderRadius: 2, p: 1.5 }}>
          We use browser location when available. If the detected city is incorrect, you can enter the correct city and state before accepting.
        </Typography>
        {locationNotice && (
          <Typography sx={{ mb: 3, color: '#5B4BDB', fontSize: 13, lineHeight: 1.7, background: '#EEF3FF', border: '1px solid #D9E4FF', borderRadius: 2, p: 1.5 }}>
            {locationNotice}
          </Typography>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <TextField label="City" value={cityOverride} onChange={(e) => setCityOverride(e.target.value)} placeholder="Enter city" size="small" sx={{ background: '#fff' }} />
          <TextField label="State" value={stateOverride} onChange={(e) => setStateOverride(e.target.value)} placeholder="Enter state" size="small" sx={{ background: '#fff' }} />
        </Box>
        <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #ECE9F4', borderRadius: 2, p: 3, mb: 3, background: '#FFF' }}>
          <Typography sx={{ whiteSpace: 'pre-wrap', color: '#1F1B2D', lineHeight: 1.8 }}>{settings.tncContent}</Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleAccept}
          disabled={accepting}
          sx={{ textTransform: 'none', fontWeight: 700, px: 4, py: 1.2, background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', '&:hover': { background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)' } }}
        >
          {accepting ? 'Recording...' : 'Accept Terms & Conditions'}
        </Button>
      </Box>
    </Box>
  );
};

export default TncReview;